/* ============================================================================
   KHP Cart Drawer v6
   Update: Listens for global 'cart:refresh' and other cart-add events so any
           custom add-to-cart implementation anywhere on the site auto-opens
           the drawer. Also intercepts XHR/fetch responses to /cart/add as a
           catch-all safety net.
   ============================================================================ */

(function () {
  'use strict';

  var CONFIG = {
    freeShippingThreshold: 10000,
    byobPageUrl: '/pages/build-a-bundle',
    upsellLimit: 4,
    toastDuration: 2400,
    byobEnabled: false, // seasonal — flip to true to bring BYOB back
    byobTiers: [
      { items: 2, percent: 10 },
      { items: 3, percent: 15 },
      { items: 4, percent: 20 },
    ],
    cartIconSelectors: [
      'a[href="/cart"]',
      '.cart-icon',
      '.header-cart',
      '.site-header__cart',
      '[data-cart-link]',
      '.minicart-link',
    ],
  };

  var $drawer, $backdrop, $panel, $count, $shipping, $shippingMsg, $shippingFill;
  var $savings, $savingsMsg;
  var $content, $empty, $items, $byobCta, $upsells, $upsellsTrack;
  var $footer, $subtotal, $toast, $toastMsg;
  var $itemTemplate, $upsellTemplate;

  var state = {
    cart: null,
    isOpen: false,
    isLoading: false,
    toastTimer: null,
  };

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '$0.00';
    var dollars = (cents / 100).toFixed(2);
    var parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function init() {
    $drawer = document.getElementById('khpCartDrawer');
    if (!$drawer) {
      console.error('[KHP Cart] Drawer element not found.');
      return;
    }

    $backdrop = $drawer.querySelector('.khp-cart-drawer__backdrop');
    $panel = $drawer.querySelector('.khp-cart-drawer__panel');
    $count = $drawer.querySelector('[data-khp-cart-count]');
    $shipping = $drawer.querySelector('[data-khp-cart-shipping]');
    $shippingMsg = $drawer.querySelector('[data-khp-cart-shipping-message]');
    $shippingFill = $drawer.querySelector('[data-khp-cart-shipping-fill]');
    $savings = $drawer.querySelector('[data-khp-cart-savings]');
    $savingsMsg = $drawer.querySelector('[data-khp-cart-savings-message]');
    $content = $drawer.querySelector('[data-khp-cart-content]');
    $empty = $drawer.querySelector('[data-khp-cart-empty]');
    $items = $drawer.querySelector('[data-khp-cart-items]');
    $byobCta = $drawer.querySelector('[data-khp-cart-byob-cta]');
    $upsells = $drawer.querySelector('[data-khp-cart-upsells]');
    $upsellsTrack = $drawer.querySelector('[data-khp-cart-upsells-track]');
    $footer = $drawer.querySelector('[data-khp-cart-footer]');
    $subtotal = $drawer.querySelector('[data-khp-cart-subtotal]');
    $toast = document.getElementById('khpCartToast');
    $toastMsg = $toast ? $toast.querySelector('[data-khp-cart-toast-message]') : null;
    $itemTemplate = document.getElementById('khpCartItemTemplate');
    $upsellTemplate = document.getElementById('khpCartUpsellTemplate');

    bindGlobalEvents();
    bindDrawerEvents();
    hijackCartIcons();
    hijackAddToCartForms();
    listenForCartEvents();      // NEW: listen for cart:refresh and similar
    interceptFetchAddToCart();  // NEW: catch fetch calls to /cart/add
    fetchCart(false);
  }

  function bindGlobalEvents() {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.isOpen) {
        closeDrawer();
      }
    });
  }

  function bindDrawerEvents() {
    $drawer.querySelectorAll('[data-khp-cart-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
  }

  function hijackCartIcons() {
    var selector = CONFIG.cartIconSelectors.join(',');
    document.body.addEventListener(
      'click',
      function (e) {
        var icon = e.target.closest(selector);
        if (!icon) return;
        if (icon.hasAttribute('data-khp-cart-skip')) return;
        if (icon.closest('[data-khp-cart-skip]')) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      },
      true
    );
  }

  function hijackAddToCartForms() {
    document.body.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || !form.matches('form')) return;
        var action = form.getAttribute('action') || '';
        if (!/\/cart\/add(\.js)?(\?|$)/.test(action)) return;
        if (form.className && form.className.indexOf('bundle-form-') !== -1) return;
        if (form.hasAttribute('data-khp-cart-skip')) return;

        e.preventDefault();
        e.stopPropagation();
        addItemFromForm(form);
      },
      true
    );
  }

  // ============================================================================
  // NEW: Listen for cart:refresh events dispatched by ANY custom add-to-cart JS
  // ============================================================================
  function listenForCartEvents() {
    var eventNames = [
      'cart:refresh',
      'cart:added',
      'cart:updated',
      'product:added',
      'ajaxCart:afterCartLoad',
    ];

    eventNames.forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        // Refresh cart state and open the drawer
        if (!state.isLoading) {
          fetchCart(true);
        }
      });
    });
  }

  // ============================================================================
  // NEW: Intercept fetch() calls to /cart/add — catches custom AJAX adds
  // that don't fire any event. After a successful add, auto-opens drawer.
  // ============================================================================
  function interceptFetchAddToCart() {
    if (typeof window.fetch !== 'function') return;

    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = args[0];

      // Determine URL string from args
      var urlString = '';
      if (typeof url === 'string') {
        urlString = url;
      } else if (url && url.url) {
        urlString = url.url;
      }

      var isAddToCart = /\/cart\/add(\.js)?(\?|$)/.test(urlString);
      var opts = args[1] || (url && url.method ? url : null);
      var isPost = opts && opts.method && opts.method.toUpperCase() === 'POST';

      var resultPromise = originalFetch.apply(this, args);

      if (isAddToCart && isPost) {
        resultPromise
          .then(function (response) {
            if (response && response.ok) {
              // Successful add — refresh cart and open drawer
              // Small delay so caller's then() handlers can run first
              setTimeout(function () {
                if (!state.isLoading) {
                  fetchCart(true);
                }
              }, 50);
            }
          })
          .catch(function () {
            // ignore — let caller handle errors
          });
      }

      return resultPromise;
    };
  }

  // ============================================================================
  // CART API CALLS
  // ============================================================================
  function fetchCart(openAfter) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        state.cart = cart;
        renderCart();
        if (openAfter) openDrawer();
        return cart;
      })
      .catch(function (err) {
        console.error('[KHP Cart] Fetch error:', err);
      });
  }

  function addItem(variantId, quantity) {
    if (state.isLoading) return Promise.resolve();
    state.isLoading = true;
    var body = new URLSearchParams();
    body.append('id', variantId);
    body.append('quantity', quantity || 1);

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: body.toString(),
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || 'Could not add to cart');
          });
        }
        return r.json();
      })
      .then(function (addedItem) {
        state.isLoading = false;
        return fetchCart(true).then(function () {
          showToast('✓ ' + (addedItem.product_title || 'Item') + ' added');
          return addedItem;
        });
      })
      .catch(function (err) {
        state.isLoading = false;
        console.error('[KHP Cart] Add error:', err);
        alert(err.message || 'Could not add to cart');
        throw err;
      });
  }

  function addItemFromForm(form) {
    var formData = new FormData(form);
    var id = formData.get('id');
    var quantity = formData.get('quantity') || 1;
    if (!id) return;

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    addItem(id, quantity).finally(function () {
      if (submitBtn) submitBtn.disabled = false;
    });
  }

  function changeQuantity(line, newQty) {
    if (state.isLoading) return;
    state.isLoading = true;

    var $line = $items.querySelector('[data-khp-cart-line][data-line="' + line + '"]');
    if ($line) $line.classList.add('is-loading');

    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ line: line, quantity: newQty }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        state.cart = cart;
        state.isLoading = false;
        renderCart();
      })
      .catch(function (err) {
        state.isLoading = false;
        if ($line) $line.classList.remove('is-loading');
        console.error('[KHP Cart] Change error:', err);
      });
  }

  function addMultipleItems(items) {
    if (state.isLoading) return Promise.resolve();
    state.isLoading = true;

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || 'Multi-add failed');
          });
        }
        return r.json();
      })
      .then(function () {
        state.isLoading = false;
        return fetchCart(true);
      })
      .catch(function (err) {
        state.isLoading = false;
        console.error('[KHP Cart] Bulk add error:', err);
        throw err;
      });
  }

  function renderCart() {
    if (!state.cart) return;
    var cart = state.cart;
    var hasItems = cart.item_count > 0;

    if ($count) $count.textContent = cart.item_count;
    updateSitewideCartCount(cart.item_count);

    if ($empty) $empty.hidden = hasItems;
    if ($footer) $footer.hidden = !hasItems;
    if ($shipping) $shipping.style.display = hasItems ? '' : 'none';

    if (hasItems) {
      renderItems(cart.items);
      renderShippingBar(cart.total_price);
      renderSavings(cart);
      renderSubtotal(cart.items_subtotal_price || cart.total_price);
      renderBYOBCTA(cart);
      loadUpsells();
    } else {
      $items.innerHTML = '';
      if ($byobCta) $byobCta.hidden = true;
      if ($upsells) $upsells.hidden = true;
      if ($savings) $savings.hidden = true;
    }
  }

  function renderItems(items) {
    $items.innerHTML = '';
    items.forEach(function (item, idx) {
      var line = idx + 1;
      var $node = $itemTemplate.content.cloneNode(true);
      var $li = $node.querySelector('[data-khp-cart-line]');
      $li.setAttribute('data-line', line);

      var url = item.url || '/products/' + item.handle;

      var $imgLink = $node.querySelector('[data-khp-cart-item-link]');
      $imgLink.setAttribute('href', url);
      var $img = $node.querySelector('[data-khp-cart-item-img]');
      if (item.image) {
        $img.src = item.image;
        $img.alt = item.product_title || '';
      }

      var $titleLink = $node.querySelector('[data-khp-cart-item-title-link]');
      $titleLink.textContent = item.product_title || item.title || '';
      $titleLink.setAttribute('href', url);

      var $variant = $node.querySelector('[data-khp-cart-item-variant]');
      if (item.variant_title && item.variant_title !== 'Default Title') {
        $variant.textContent = item.variant_title;
        $variant.hidden = false;
      }

      var $discountTag = $node.querySelector('[data-khp-cart-item-discount-tag]');
      var $discountText = $node.querySelector('[data-khp-cart-item-discount-tag-text]');
      var $comparePrice = $node.querySelector('[data-khp-cart-item-compare-price]');
      var $price = $node.querySelector('[data-khp-cart-item-price]');

      var hasDiscount = item.original_line_price > item.final_line_price;

      if (hasDiscount) {
        var discountTitle = '';
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
          var alloc = item.line_level_discount_allocations[0];
          if (alloc.discount_application && alloc.discount_application.title) {
            discountTitle = String(alloc.discount_application.title).trim();
          }
        }
        if (!discountTitle && item.discounts && item.discounts.length) {
          if (item.discounts[0].title) {
            discountTitle = String(item.discounts[0].title).trim();
          }
        }

        if (discountTitle && $discountTag && $discountText) {
          $discountText.textContent = discountTitle;
          $discountTag.hidden = false;
        }

        if ($comparePrice) {
          $comparePrice.textContent = formatMoney(item.original_line_price);
          $comparePrice.hidden = false;
        }
      }

      $price.textContent = formatMoney(item.final_line_price);

      var $qtyValue = $node.querySelector('[data-khp-cart-qty-value]');
      $qtyValue.textContent = item.quantity;

      var $decrease = $node.querySelector('[data-khp-cart-qty-decrease]');
      var $increase = $node.querySelector('[data-khp-cart-qty-increase]');
      var $remove = $node.querySelector('[data-khp-cart-remove]');

      $decrease.addEventListener('click', function () {
        if (item.quantity > 1) changeQuantity(line, item.quantity - 1);
        else changeQuantity(line, 0);
      });
      $increase.addEventListener('click', function () {
        changeQuantity(line, item.quantity + 1);
      });
      $remove.addEventListener('click', function () {
        changeQuantity(line, 0);
      });

      $items.appendChild($node);
    });
  }

  function renderShippingBar(currentPrice) {
    var threshold = CONFIG.freeShippingThreshold;
    var remaining = Math.max(0, threshold - currentPrice);
    var percent = Math.min(100, (currentPrice / threshold) * 100);

    if (remaining === 0) {
      $shipping.classList.add('is-qualified');
      $shippingMsg.innerHTML = '🚚 <strong>FREE shipping</strong> unlocked!';
    } else {
      $shipping.classList.remove('is-qualified');
      $shippingMsg.innerHTML =
        "You're <strong>" + formatMoney(remaining) + '</strong> away from <strong>FREE shipping</strong>';
    }
    $shippingFill.style.width = percent + '%';
  }

  function renderSavings(cart) {
    if (!$savings || !$savingsMsg) return;

    if (cart.total_discount > 0) {
      $savings.hidden = false;
      $savings.classList.remove('is-projected');

      var discountText = '';
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
        if (cart.cart_level_discount_applications[0].title) {
          discountText = String(cart.cart_level_discount_applications[0].title).trim();
        }
      } else {
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
            var alloc = item.line_level_discount_allocations[0];
            if (alloc.discount_application && alloc.discount_application.title) {
              discountText = String(alloc.discount_application.title).trim();
              if (discountText) break;
            }
          }
          if (item.discounts && item.discounts.length && item.discounts[0].title) {
            discountText = String(item.discounts[0].title).trim();
            if (discountText) break;
          }
        }
      }

      $savingsMsg.innerHTML =
        '🎉 You\'re saving <strong>' + formatMoney(cart.total_discount) + '</strong>' +
        (discountText ? ' with ' + discountText : '');
      return;
    }

    var itemCount = cart.item_count;
    var nextTier = CONFIG.byobEnabled
      ? CONFIG.byobTiers.find(function (tier) {
          return tier.items > itemCount;
        })
      : null;

    if (nextTier && itemCount > 0) {
      var itemsToAdd = nextTier.items - itemCount;
      $savings.hidden = false;
      $savings.classList.add('is-projected');
      $savingsMsg.innerHTML =
        'Add <strong>' +
        itemsToAdd +
        ' more</strong> to save <strong>' +
        nextTier.percent +
        '%</strong> on your bundle';
      return;
    }

    $savings.hidden = true;
  }

  function renderSubtotal(amount) {
    if ($subtotal) $subtotal.textContent = formatMoney(amount);
  }

  function renderBYOBCTA(cart) {
    if (!$byobCta) return;
    var hasBundleItem = cart.items.some(function (item) {
      var url = (item.url || '').toLowerCase();
      var title = (item.product_title || '').toLowerCase();
      return url.indexOf('bundle') !== -1 || title.indexOf('bundle') !== -1;
    });
    var onBYOBPage = window.location.pathname.indexOf('/pages/build-a-bundle') === 0;
    $byobCta.hidden = hasBundleItem || onBYOBPage;
  }

  function loadUpsells() {
    if (!$upsells || !$upsellsTrack || !state.cart || !state.cart.items.length) return;
    var productId = state.cart.items[0].product_id;
    var url =
      '/recommendations/products.json?product_id=' +
      productId +
      '&limit=' +
      CONFIG.upsellLimit +
      '&intent=related';

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (!data.products || !data.products.length) {
          $upsells.hidden = true;
          return;
        }
        var cartProductIds = state.cart.items.map(function (i) {
          return i.product_id;
        });
        var filtered = data.products.filter(function (p) {
          return cartProductIds.indexOf(p.id) === -1;
        });
        if (!filtered.length) {
          $upsells.hidden = true;
          return;
        }
        renderUpsells(filtered.slice(0, CONFIG.upsellLimit));
        $upsells.hidden = false;
      })
      .catch(function (err) {
        console.warn('[KHP Cart] Upsells unavailable:', err);
        $upsells.hidden = true;
      });
  }

  function renderUpsells(products) {
    $upsellsTrack.innerHTML = '';
    products.forEach(function (product) {
      var $node = $upsellTemplate.content.cloneNode(true);
      var url = product.url || '/products/' + product.handle;

      var $imgLink = $node.querySelector('[data-khp-upsell-link]');
      $imgLink.href = url;
      var $img = $node.querySelector('[data-khp-upsell-img]');
      if (product.featured_image) {
        $img.src = product.featured_image;
        $img.alt = product.title || '';
      } else if (product.media && product.media[0]) {
        $img.src = product.media[0].preview_image.src;
        $img.alt = product.title || '';
      }

      var $titleLink = $node.querySelector('[data-khp-upsell-title-link]');
      $titleLink.textContent = product.title || '';
      $titleLink.href = url;

      var $price = $node.querySelector('[data-khp-upsell-price]');
      $price.textContent = formatMoney(product.price);

      var $addBtn = $node.querySelector('[data-khp-upsell-add]');
      var firstVariantId = product.variants && product.variants[0] && product.variants[0].id;
      if (firstVariantId) {
        $addBtn.addEventListener('click', function () {
          $addBtn.classList.add('is-loading');
          $addBtn.textContent = 'Adding...';
          addItem(firstVariantId, 1)
            .then(function () {
              $addBtn.classList.remove('is-loading');
              $addBtn.textContent = '+ Add';
            })
            .catch(function () {
              $addBtn.classList.remove('is-loading');
              $addBtn.textContent = '+ Add';
            });
        });
      } else {
        $addBtn.disabled = true;
      }

      $upsellsTrack.appendChild($node);
    });
  }

  function openDrawer() {
    state.isOpen = true;
    $drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('khp-cart-drawer-open');
  }

  function closeDrawer() {
    state.isOpen = false;
    $drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('khp-cart-drawer-open');
  }

  function updateSitewideCartCount(count) {
    var selectors = [
      '.cart-count',
      '.header-cart-count',
      '.site-header__cart-count',
      '[data-cart-count]',
      '.cart_count',
      '.cartCount',
      '#CartCount',
      '.minicart-count',
      '.header-cart .count',
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.hasAttribute('data-khp-cart-count')) return;
        el.textContent = count;
      });
    });
  }

  function showToast(message) {
    if (!$toast || !$toastMsg) return;
    clearTimeout(state.toastTimer);
    $toastMsg.textContent = message;
    $toast.hidden = false;
    void $toast.offsetWidth;
    $toast.classList.add('is-visible');
    state.toastTimer = setTimeout(function () {
      $toast.classList.remove('is-visible');
      setTimeout(function () {
        $toast.hidden = true;
      }, 400);
    }, CONFIG.toastDuration);
  }

  window.KHPCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: function () {
      return fetchCart(false);
    },
    refreshAndOpen: function () {
      return fetchCart(true);
    },
    addItem: addItem,
    addMultipleItems: addMultipleItems,
    getCart: function () {
      return state.cart;
    },
    showToast: showToast,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// ============================================
// FLEX FACTOR CROSS-SELL POPUP  (v2)
//
// Drop-in replacement for the FFX block at the bottom of khp-cart-drawer.js.
// Everything above the "KHP Cart Drawer v6" IIFE stays exactly as-is —
// replace only from "// FLEX FACTOR CROSS-SELL POPUP" to the end of file.
//
// Fixes:
//   1. The x and the backdrop now set a cooldown. Previously only the
//      "No thanks" button called markDeclined(), so closing with the x
//      wrote nothing and the popup returned on the very next add.
//   2. Triggers on the product that was JUST ADDED, not on cart contents.
//      The handle is read out of the /cart/add.js response, so this works
//      on the BTS landing page, PDPs, bundles — every surface, no wiring.
//   3. /cart/change no longer triggers it. Changing a quantity or removing
//      an item was re-opening the popup.
//   4. Won't re-open while already open.
// ============================================
(function () {
  'use strict';

  // ==========================================================================
  // KILL SWITCH — OFF for Back to School week (8/10-8/16/2026)
  //
  // Turned off because the BTS tier discount (20/30/40) and the Flex Factor
  // Launch Combo are both automatic discounts, and Shopify applies only one
  // unless combinations are configured. Rather than have the popup promise
  // $12.99 that checkout may not honor, it's parked until the sale ends.
  //
  // TO BRING IT BACK: set FFX_ENABLED = true (after 8/16, and after the
  // discount combination question is settled).
  // ==========================================================================
  var FFX_ENABLED = false;

  // Optional auto-resume instead of remembering: set FFX_ENABLED = true above
  // and uncomment the next line to hold it off until the sale is over.
  // if (Date.now() < new Date('2026-08-17T00:00:00-04:00').getTime()) return;

  if (!FFX_ENABLED) return;

  var FLEX_FACTOR_HANDLE = 'curl-math-flex-factor-flexible-holding-spray-8oz';
  var FLEX_FACTOR_VARIANT_ID = null;
  var CURL_MATH_HANDLE_PREFIX = 'curl-math-';
  var HIDE_ON_PATHS = [
    '/pages/curl-math-flex-factor-hairspray',
    '/products/curl-math-flex-factor-flexible-holding-spray-8oz'
  ];

  // Graduated cooldowns. An explicit "No thanks" is a real signal and earns
  // the full 72 hours. An x or backdrop click is often a misclick, so it
  // rests the offer rather than killing it for three days.
  var DECLINE_COOLDOWN_HOURS = 72;
  var DISMISS_COOLDOWN_HOURS = 6;

  var TRIGGER_DELAY_MS = 1500;
  var STORAGE_KEY  = 'ffxDeclinedAt';
  var COOLDOWN_KEY = 'ffxCooldownHours';
  var SESSION_KEY  = 'ffxAddedThisSession';

  var popup  = document.getElementById('ffx-popup');
  var addBtn = document.getElementById('ffx-popup-add');
  if (!popup || !addBtn) return;

  var triggerTimer = null;
  var flexFactorVariantIdCache = FLEX_FACTOR_VARIANT_ID;

  function isOnFlexFactorPage() {
    return HIDE_ON_PATHS.some(function (path) {
      return window.location.pathname.indexOf(path) === 0;
    });
  }

  function isCurlMathHandle(h) {
    h = h || '';
    return h.indexOf(CURL_MATH_HANDLE_PREFIX) === 0 && h !== FLEX_FACTOR_HANDLE;
  }

  function hasFlexFactorInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return item.handle === FLEX_FACTOR_HANDLE;
    });
  }

  function isCoolingDown() {
    try {
      var stamp = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      if (isNaN(stamp)) return false;
      var hours = parseFloat(localStorage.getItem(COOLDOWN_KEY));
      if (isNaN(hours)) hours = DECLINE_COOLDOWN_HOURS;
      return (Date.now() - stamp) / 3600000 < hours;
    } catch (e) { return false; }
  }

  function wasAddedThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; }
    catch (e) { return false; }
  }

  function startCooldown(hours) {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      localStorage.setItem(COOLDOWN_KEY, String(hours));
    } catch (e) {}
  }

  function markAdded() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  // triggerHandle = the product that was just added. Required.
  function shouldShow(cart, triggerHandle) {
    if (popup.classList.contains('is-open')) return false;
    if (isOnFlexFactorPage())      return false;
    if (wasAddedThisSession())     return false;
    if (isCoolingDown())           return false;
    if (hasFlexFactorInCart(cart)) return false;
    if (!isCurlMathHandle(triggerHandle)) return false;
    return true;
  }

  function openPopup() {
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
  }

  function closePopup() {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
  }

  function getFlexFactorVariantId() {
    if (flexFactorVariantIdCache) return Promise.resolve(flexFactorVariantIdCache);
    return fetch('/products/' + FLEX_FACTOR_HANDLE + '.js')
      .then(function (res) { return res.json(); })
      .then(function (product) {
        flexFactorVariantIdCache = product.variants[0].id;
        return flexFactorVariantIdCache;
      });
  }

  function checkAndTrigger(triggerHandle) {
    if (!isCurlMathHandle(triggerHandle)) return;   // bail before the network call
    if (triggerTimer) clearTimeout(triggerTimer);

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!shouldShow(cart, triggerHandle)) return;
        triggerTimer = setTimeout(openPopup, TRIGGER_DELAY_MS);
      })
      .catch(function () {});
  }

  // ---- add ----------------------------------------------------------------
  addBtn.addEventListener('click', function () {
    addBtn.disabled = true;
    var originalText = addBtn.textContent;
    addBtn.textContent = 'Adding...';

    getFlexFactorVariantId()
      .then(function (variantId) {
        if (window.KHPCart && typeof window.KHPCart.addItem === 'function') {
          return window.KHPCart.addItem(variantId, 1);
        }
        var body = new URLSearchParams();
        body.append('id', variantId);
        body.append('quantity', 1);
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: body.toString()
        });
      })
      .then(function () {
        addBtn.textContent = 'Added';
        markAdded();
        setTimeout(function () {
          closePopup();
          addBtn.disabled = false;
          addBtn.textContent = originalText;
        }, 800);
      })
      .catch(function (err) {
        console.error('FFX add error:', err);
        addBtn.textContent = 'Try again';
        setTimeout(function () {
          addBtn.disabled = false;
          addBtn.textContent = originalText;
        }, 1500);
      });
  });

  // ---- close: EVERY path sets a cooldown ----------------------------------
  popup.querySelectorAll('[data-ffx-close]').forEach(function (el) {
    el.addEventListener('click', function () {
      startCooldown(
        el.hasAttribute('data-ffx-decline') ? DECLINE_COOLDOWN_HOURS : DISMISS_COOLDOWN_HOURS
      );
      closePopup();
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) {
      startCooldown(DISMISS_COOLDOWN_HOURS);
      closePopup();
    }
  });

  // ---- trigger: read the handle out of the add response --------------------
  // /cart/add.js returns the added line item (single add) or {items:[...]}
  // (bulk add). Keying off that means the popup responds to what was actually
  // added, on every surface, without each one having to announce itself.
  function handlesFromAddResponse(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) {
      return data.items.map(function (i) { return i.handle; }).filter(Boolean);
    }
    return data.handle ? [data.handle] : [];
  }

  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var urlString = typeof url === 'string' ? url : (url && url.url) || '';
    var result = originalFetch.apply(this, arguments);

    // /cart/add ONLY. /cart/change used to be watched here, which meant
    // removing an item or nudging a quantity re-opened the popup.
    if (urlString.indexOf('/cart/add') !== -1) {
      result
        .then(function (response) {
          if (!response || !response.ok || typeof response.clone !== 'function') return;
          return response.clone().json().then(function (data) {
            var trigger = handlesFromAddResponse(data).filter(isCurlMathHandle)[0];
            if (trigger) setTimeout(function () { checkAndTrigger(trigger); }, 300);
          });
        })
        .catch(function () {});
    }

    return result;
  };

  // Manual hooks for testing.
  //   FFX.reset()                              clear all cooldowns
  //   FFX.trigger('curl-math-solve-for-...')   force a check
  window.FFX = {
    trigger: checkAndTrigger,
    reset: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(COOLDOWN_KEY);
        sessionStorage.removeItem(SESSION_KEY);
      } catch (e) {}
      console.log('[FFX] cooldowns cleared');
    }
  };
})();
